import { ErrorState, Pagination, Table, TableFilters, TableSearch, TableSortMenu, TableToolbar } from '@cinesend/ui-kit'
import { TABLE_PAGE_SIZES } from '../../constants/table_page_sizes'

// useTableFilters keeps sort directions as 'ASC'/'DESC' and zero-based pages; the kit uses 'asc'/'desc' and one-based pages.
const DataTable = ({
  caption,
  columns,
  rows,
  rowKey = (row) => String(row.id),
  onRowClick,
  isLoading = false,
  error,
  onRetry,
  empty,
  searching,
  sorting,
  filtering,
  filterControl,
  actions,
  pagination,
  totalRows = 0,
  selectedKeys,
  onSelectionChange,
  selectAllLabel,
  selectionLabel,
}) => {
  const sort = sorting?.direction ? { key: sorting.key, direction: sorting.direction.toLowerCase() } : null
  const onSortChange = (next) => sorting.onSortChange({ key: next.key, direction: next.direction.toUpperCase() })
  const sortKeys = sorting?.options.map((option) => option.key) ?? []
  const filterGroups = filtering?.options.map((group) => ({
    key: group.key,
    label: group.label,
    options: (group.options ?? []).map((option) => ({ value: String(option.key), label: option.label })),
  }))
  const page = pagination ? pagination.currentPage + 1 : 1
  const from = pagination && rows.length ? (page - 1) * pagination.rowsPerPage + 1 : null

  return (
    <>
      {(searching || sorting || filtering || actions) && (
        <TableToolbar
          actions={
            <>
              {filterControl}
              {!filterControl && filtering && (
                <TableFilters
                  groups={filterGroups}
                  value={filtering.filters ?? {}}
                  onValueChange={(next) =>
                    filtering.onFiltersChange(
                      Object.fromEntries(Object.entries(next).filter(([, values]) => values.length))
                    )
                  }
                />
              )}
              {sorting && <TableSortMenu options={sorting.options} sort={sort} onSortChange={onSortChange} />}
              {actions}
            </>
          }
        >
          {searching && (
            <TableSearch
              value={searching.search}
              onValueChange={searching.onSearch}
              placeholder={
                searching.searchPlaceholder && searching.searchPlaceholder !== 'Search...'
                  ? searching.searchPlaceholder
                  : `Search ${caption.toLowerCase()}`
              }
            />
          )}
        </TableToolbar>
      )}
      {error ? (
        <ErrorState
          message={error.message ?? 'An error occurred. Please contact customer support.'}
          onRetry={onRetry}
        />
      ) : (
        <Table
          caption={caption}
          columns={columns.map((column) => ({ ...column, sortable: sortKeys.includes(column.key) }))}
          rows={rows}
          rowKey={rowKey}
          onRowClick={onRowClick}
          sort={sort}
          onSortChange={sorting ? onSortChange : undefined}
          isLoading={isLoading}
          empty={empty}
          selectedKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          selectAllLabel={selectAllLabel}
          selectionLabel={selectionLabel}
        />
      )}
      {pagination && (
        <Pagination
          compact
          page={page}
          pageCount={Math.ceil(totalRows / pagination.rowsPerPage) || 1}
          from={from}
          to={from ? from + rows.length - 1 : null}
          total={totalRows}
          pageSize={pagination.onRowsPerPageChange ? pagination.rowsPerPage : undefined}
          pageSizeOptions={TABLE_PAGE_SIZES}
          onPageSizeChange={pagination.onRowsPerPageChange}
          onPage={(next) => pagination.onPageChange(next - 1)}
          isFetching={isLoading}
        />
      )}
    </>
  )
}

export default DataTable
